import styles from './filters.module.css'
import { FieldType, FieldValue, FilterField } from '../types'
import { Field } from '../fields/field/field'
import { Button } from '../button/button'
import { useEffect, useMemo, useState } from 'react'
import { useLocalStorageActions } from '../../actions/localStorageActions'
import { isDefined } from '../../../../utils/typeUtils'
import { Spinner } from '../spinner/spinner'

type Props = {
    filtersKey: string
    searchField?: FilterField
    fields?: FilterField[]
    firstRowLength?: number
}

type FilterKeys = {
    [key: string]: number
}

export const Filters = (props: Props) => {
    const { filtersKey, searchField, fields, firstRowLength = 4 } = props

    const hasDefaultValues = useMemo(() => fields?.some(field => isDefined(field.defaultValue || field.value)), [])
    const [showFilters, setShowFilters] = useState<boolean>(hasDefaultValues || !searchField)
    const [filterFields, setFilterFields] = useState<FilterField[]>([])
    const initialFilterKeys: FilterKeys = useMemo(() => (fields || []).reduce((acc, field) => ({ ...acc, [field.name]: 0 }), {}), [])
    const [filterKeys, setFilterKeys] = useState<FilterKeys>(initialFilterKeys)

    const { getFiltersData, saveFiltersData } = useLocalStorageActions()

    const onClickFilters = () => setShowFilters(prevValue => !prevValue)

    const showClassName = showFilters ? styles.show : ''

    const onFieldChange = (name: string, value?: FieldValue) => {
        const filtersData = getFiltersData()
        const filterData = filtersData[filtersKey]
        const newFiltersData = {
            ...filtersData,
            [filtersKey]: {
                ...filterData,
                [name]: value
            }
        }
        saveFiltersData(newFiltersData)
    }

    useEffect(() => {
        const newFields = (fields || []).map(field => ({
            ...field,
            onChange: (value?: FieldValue) => {
                onFieldChange(field.name, value)
                field.onChange && field.onChange(value)
            }
        }))
        setFilterFields(newFields)
    }, [fields, firstRowLength])

    const onClear = (fieldName: string) => () => {
        const newFields = filterFields.map(field => field.name === fieldName ? ({
            ...field,
            defaultValue: undefined
        }) : field)
        setFilterFields(newFields)

        const field = filterFields.find(field => field.name === fieldName)
        field?.onChange(undefined)

        setFilterKeys(prevKeys => ({
            ...prevKeys,
            [fieldName]: prevKeys[fieldName]! + 1
        }))
    }

    const onClearAll = () => {
        const newFields = filterFields.map(field => ({
            ...field,
            defaultValue: undefined,
        }))
        setFilterFields(newFields)
        filterFields.forEach(field => field.onChange(undefined))
        setFilterKeys(prevKeys => Object.fromEntries(Object.entries(prevKeys).map(([key, value]) => [key, value + 1])))
    }

    const fieldRows = useMemo(() => filterFields.length > 4 ?
        [filterFields.slice(0, firstRowLength), filterFields.slice(firstRowLength, filterFields.length)] :
        [filterFields]
    , [filterFields])

    return (
        <div className={styles.container}>
            <div className={styles.row}>
                {searchField && (
                    <div className={`col-sm-${searchField.space || 5}`}>
                        <Field
                            field={searchField}
                            onChange={searchField.onChange}
                        />
                    </div>
                )}
                {fields && (
                    <>
                    <Button
                        icon='filter'
                        label="Filtros"
                        kind={showFilters ? 'primary' : 'tertiary'}
                        small
                        onClick={onClickFilters}
                    />
                    {showFilters && (
                        <Button
                            icon='x-lg'
                            label="Limpiar"
                            kind='tertiary'
                            small
                            onClick={onClearAll}
                        />
                    )}
                    </>
                )}
            </div>
            {fields && fieldRows.map((fieldRow, index) => (
                <div key={index} className={`${styles.row} ${styles.filtersRow} ${showClassName}`}>
                    {fieldRow?.map(field => {
                        const { name, label, space = 4, onChange } = field
                        const renderedField = field.type === FieldType.BOOLEAN ? { ...field, label: undefined } : field
                        return (
                            <div key={`${name}-filter`} className={`col-sm-${space}`}>
                                {label && (
                                    <label className={styles.filterLabel}>{label}</label>
                                )}
                                {field.loading ? <Spinner centered={false} /> : (
                                    <div className={styles.filterContainer}>
                                        <Field
                                            key={filterKeys[name]}
                                            field={renderedField}
                                            onChange={onChange}
                                            className={styles.filter}
                                        />
                                        {!field.required && (
                                            <Button
                                                icon='x-lg'
                                                kind='tertiary'
                                                small
                                                onClick={onClear(field.name)}
                                                className={styles.clearButton}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            ))}
        </div>
    )
}
